'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Home, Heart, UserPlus } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { buildLocalePath, type Locale } from '@/i18n/config';

const DEFAULT_HERO_IMAGE_URL = '/images/dgr/HeroImage.jpg';

export interface NotFoundPageProps {
  tenantName?: string;
  tenantSlug?: string;
  homeHref?: string;
  showSearch?: boolean;
  heroImageUrl?: string;
}

export function NotFoundPage({
  homeHref = '/',
  heroImageUrl = DEFAULT_HERO_IMAGE_URL,
  tenantSlug,
}: NotFoundPageProps) {
  const t = useTranslations('notFound');
  const locale = useLocale() as Locale;
  const localizedHomeHref = buildLocalePath(locale, homeHref);
  const isDgd = tenantSlug === 'dgd';
  // Tenant-neutral filled style: cream on dark page reads cleanly without
  // depending on tenant-scoped CSS variables (which 404 routes may render
  // outside of when no tenant header is present).
  const accentBtn = isDgd
    ? 'bg-dgd-gold hover:bg-dgd-gold-hover text-dgd-charcoal'
    : 'bg-stone-100 hover:bg-white text-stone-950';

  return (
    <div className="bg-stone-950 text-white">
      {/* Hero with 404 overlay */}
      <div className="relative h-[40vh] min-h-[280px] flex items-center justify-center overflow-hidden">
        <Image src={heroImageUrl} alt="" fill className="object-cover brightness-50" priority />
        <span className="relative text-7xl md:text-8xl font-bold tracking-wider text-white/80">
          404
        </span>
      </div>

      {/* Content */}
      <div className="px-4 py-16 md:py-24">
        <div className="max-w-lg mx-auto text-center space-y-6">
          <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight">{t('title')}</h1>

          <p className="text-lg text-stone-400">{t('description')}</p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <Button size="lg" className={accentBtn} asChild>
              <Link href={localizedHomeHref}>
                <Home className="mr-2 h-4 w-4" />
                {t('goHome')}
              </Link>
            </Button>

            <Button
              size="lg"
              variant="outline"
              className="border-stone-700 bg-transparent text-white hover:bg-stone-800 hover:text-white"
              asChild
            >
              <Link href={buildLocalePath(locale, '/donate')}>
                <Heart className="mr-2 h-4 w-4" />
                {t('donate')}
              </Link>
            </Button>

            <Button
              size="lg"
              variant="outline"
              className="border-stone-700 bg-transparent text-white hover:bg-stone-800 hover:text-white"
              asChild
            >
              <Link href={buildLocalePath(locale, '/register')}>
                <UserPlus className="mr-2 h-4 w-4" />
                {t('registerCta')}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
